import {
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type MouseEvent as ReactMouseEvent,
  type RefObject,
} from "react";
import { useCanvasStore } from "../store/canvas-store";
import { ZoomButtons } from "./zoom-buttons";
import { useCtrlWheelZoom } from "../input/use-ctrl-wheel-zoom";
import { useCanvasRect } from "../use-canvas-rect";
import {
  BOTTOM_CONTROLS_HEIGHT_VARIABLE,
  usePanelSizeReserve,
} from "../use-panel-size-reserve";

// 右下のミニマップ（全体地図 MAP）。
// - 10240x5760 キャンバス全体を縮小表示し、現在のビューポート位置を四角で囲って表示する
// - ミニマップ内の余白をクリックすると、該当位置がビューポート中央になるよう pan する
// - ビューポート枠をドラッグすると、枠の位置を直接ずらすようにビューポートが移動する
//   （クリック単独の挙動は維持し、ドラッグ操作のみ追加。マウス押下時の枠中心からの相対位置を維持する。）
// - 下段に - / + ズームボタンと、Connect Feedback の FAB 用の空き領域を配置する
//   （全体マップの外枠の左端をズームパネルの左端へ、右端を FAB の右端へそろえる。配置の正本は
//   styles.css の .canvas-bottom-controls 系クラスと --canvas-bottom-controls-* 変数）
// - 全体マップの大きさは下段の実幅で決まり、ルート文字サイズや画面幅で変わる。描画・クリック・
//   ドラッグの換算はいずれも、実測した内部（content box）の幅・高さを使う

// ドラッグ判定の閾値（ミニマップ内ピクセル）。これ未満の移動はクリック扱いに戻す。
const DRAG_THRESHOLD_PX = 2;
// 全体マップの内部（content box）を実測できるまでに使う仮の幅。マウント後の実測値へ置き換える。
// 従来の固定幅（枠線込み 220px）の内部幅に合わせる。
const FALLBACK_CONTENT_WIDTH_PX = 218;
// 下段の必要幅と上限幅を比べるときの許容差。小数の丸めだけで狭い表示と誤判定しないため。
const COMPACT_TOLERANCE_PX = 0.5;

/** 全体マップの内部（content box）の寸法。 */
interface ContentSize {
  readonly width: number;
  readonly height: number;
}

/**
 * 下段（ズームパネル + 間隔 + FAB 用の空き領域）が、画面の左右の余白の内側
 * （下部コントロールの根要素の max-width）へ収まらないかを判定する。
 * 収まらない狭い表示では、ズームパネルを折り返して FAB と重ねず、全体マップを隠す。
 * 上限幅を解釈できない（スタイルが適用されていない環境など）場合は収まるものとして扱う。
 */
export function needsCompactBottomControls(input: {
  readonly zoomWidth: number;
  readonly gap: number;
  readonly fabSlotWidth: number;
  readonly availableWidth: number;
}): boolean {
  if (!Number.isFinite(input.availableWidth)) return false;
  return (
    input.zoomWidth + input.gap + input.fabSlotWidth > input.availableWidth + COMPACT_TOLERANCE_PX
  );
}

export function MiniMap({ thumbnailDataUrl }: { readonly thumbnailDataUrl?: string | undefined }): JSX.Element {
  const view = useCanvasStore((s) => s.view);
  const setView = useCanvasStore((s) => s.setView);
  // 現在のキャンバス矩形（サイズプリセットに応じた原点＋寸法）。ステージ↔ミニマップ変換の基準にする。
  // handler 内の getBoundingClientRect() が返す DOM 矩形（同名 rect）と衝突しないよう canvasRect とする。
  const canvasRect = useCanvasRect();
  // ビューポート（screen 座標で表示中の領域）が stage 座標でどこにあたるかを概算
  const viewportWidthInStage = window.innerWidth / view.zoom;
  const viewportHeightInStage = window.innerHeight / view.zoom;
  const viewportLeftInStage = -view.panX / view.zoom;
  const viewportTopInStage = -view.panY / view.zoom;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<HTMLDivElement | null>(null);
  const zoomRef = useRef<HTMLDivElement | null>(null);
  const fabSlotRef = useRef<HTMLDivElement | null>(null);
  const compact = useCompactBottomControls(rootRef, zoomRef, fabSlotRef);
  // 狭い表示の切り替え（全体マップの表示・非表示）でも測り直す。ResizeObserver が無い環境では、
  // 切り替えの原因になった画面サイズの変更時の実測が、表示の切り替え前の寸法（0）になるため。
  const mapSize = useContentBoxSize(
    mapRef,
    {
      width: FALLBACK_CONTENT_WIDTH_PX,
      height: (FALLBACK_CONTENT_WIDTH_PX * canvasRect.h) / canvasRect.w,
    },
    compact
  );
  // 内部寸法が 0（全体マップを隠している・未計測）の間は、換算で 0 除算しないよう操作を受け付けない。
  const measured = mapSize.width > 0 && mapSize.height > 0;
  const ratioX = mapSize.width / canvasRect.w;
  const ratioY = mapSize.height / canvasRect.h;
  const frameLeftPx = Math.max(0, (viewportLeftInStage - canvasRect.x) * ratioX);
  const frameTopPx = Math.max(0, (viewportTopInStage - canvasRect.y) * ratioY);

  // ビューポート枠ドラッグ中の状態。ドラッグ判定が成立した場合、直後の click を 1 回抑止する。
  const dragStateRef = useRef<{
    startLocalX: number;
    startLocalY: number;
    grabOffsetX: number;
    grabOffsetY: number;
    moved: boolean;
  } | null>(null);
  const swallowNextClickRef = useRef<boolean>(false);
  // ミニマップ / ズームボタン上での Ctrl+ホイールもキャンバスズームに振り替える（ブラウザ拡大を防ぐ）。
  useCtrlWheelZoom(rootRef);
  // 画面右下のお知らせを下部コントロールの上へ出すため、実測高さを CSS 変数へ渡す
  // （全体マップの大きさはルート文字サイズや画面幅で変わり、狭い表示では隠れる）。
  usePanelSizeReserve(rootRef, BOTTOM_CONTROLS_HEIGHT_VARIABLE, "height");

  // ミニマップ余白クリック → 該当ステージ座標がビューポート中心になるよう pan を更新する（既存挙動）。
  // ビューポート枠のドラッグ直後に発火する click は枠位置を上書きしてしまうため、1 回だけ抑止する。
  const handleMapClick = (event: ReactMouseEvent<HTMLDivElement>): void => {
    if (swallowNextClickRef.current) {
      swallowNextClickRef.current = false;
      return;
    }
    if (!measured) return;
    const local = toMapLocal(event.currentTarget, event.clientX, event.clientY);
    const stageX = local.x / ratioX + canvasRect.x;
    const stageY = local.y / ratioY + canvasRect.y;
    setView({
      ...view,
      panX: window.innerWidth / 2 - stageX * view.zoom,
      panY: window.innerHeight / 2 - stageY * view.zoom,
    });
  };

  // ビューポート枠 pointerdown: ドラッグ開始。掴んだ位置と枠左上の相対オフセットを保存し、
  // 移動中はそのオフセットを保ったまま枠を追従させる（つまみ感のあるドラッグ）。
  const onFramePointerDown = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (event.button !== 0) return;
    if (!measured) return;
    const mapElement = event.currentTarget.parentElement;
    if (!mapElement) return;
    const local = toMapLocal(mapElement, event.clientX, event.clientY);
    dragStateRef.current = {
      startLocalX: local.x,
      startLocalY: local.y,
      grabOffsetX: local.x - frameLeftPx,
      grabOffsetY: local.y - frameTopPx,
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    // 親 div の onClick が同時に発火しないよう、枠側でのドラッグ操作を独立させる。
    event.stopPropagation();
  };

  const onFramePointerMove = (event: ReactPointerEvent<HTMLDivElement>): void => {
    const dragState = dragStateRef.current;
    if (!dragState) return;
    // ドラッグ中に全体マップが隠れた（内部寸法が 0 になった）場合は換算できないため動かさない。
    if (!measured) return;
    const mapElement = event.currentTarget.parentElement;
    if (!mapElement) return;
    const local = toMapLocal(mapElement, event.clientX, event.clientY);
    const distance = Math.hypot(local.x - dragState.startLocalX, local.y - dragState.startLocalY);
    if (!dragState.moved && distance <= DRAG_THRESHOLD_PX) return;
    dragState.moved = true;
    // 枠左上 (= mouse - grabOffset) を stage 座標へ写し、そこから panX/panY を逆算する。
    // ratioX/Y は「実測した内部寸法 / キャンバス寸法」なので 1/ratio で stage 座標へ戻す。
    const nextFrameLeftPx = local.x - dragState.grabOffsetX;
    const nextFrameTopPx = local.y - dragState.grabOffsetY;
    const stageLeft = nextFrameLeftPx / ratioX + canvasRect.x;
    const stageTop = nextFrameTopPx / ratioY + canvasRect.y;
    setView({
      ...view,
      panX: -stageLeft * view.zoom,
      panY: -stageTop * view.zoom,
    });
  };

  const onFramePointerUp = (event: ReactPointerEvent<HTMLDivElement>): void => {
    const dragState = dragStateRef.current;
    if (!dragState) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    // ドラッグ成立後に続けて発火する click（同じ pointerdown/up シーケンス）で
    // 親 div の click-to-center が走るのを抑止する。
    if (dragState.moved) {
      swallowNextClickRef.current = true;
    }
    dragStateRef.current = null;
  };

  return (
    <div
      ref={rootRef}
      className="canvas-bottom-controls"
      data-testid="canvas-bottom-controls"
      data-compact={compact ? "" : undefined}
    >
      <div
        ref={mapRef}
        data-testid="mini-map"
        className="canvas-mini-map"
        onClick={handleMapClick}
        style={{
          // 縦横比はキャンバスの縦横比にそろえる（内部 = content box に掛かる。styles.css の
          // .canvas-mini-map）。幅は下段の実幅から決まる。
          aspectRatio: `${canvasRect.w} / ${canvasRect.h}`,
          background: thumbnailDataUrl
            ? `#ffffff url(${thumbnailDataUrl}) center/cover no-repeat`
            : "var(--color-surface)",
        }}
      >
        <div
          data-testid="mini-map-viewport-frame"
          onPointerDown={onFramePointerDown}
          onPointerMove={onFramePointerMove}
          onPointerUp={onFramePointerUp}
          onPointerCancel={onFramePointerUp}
          style={{
            position: "absolute",
            left: `${frameLeftPx}px`,
            top: `${frameTopPx}px`,
            width: `${Math.min(mapSize.width, viewportWidthInStage * ratioX)}px`,
            height: `${Math.min(mapSize.height, viewportHeightInStage * ratioY)}px`,
            border: "2px solid var(--color-accent)",
            borderRadius: "2px",
            // ドラッグ操作を受け取れるよう pointerEvents を有効化し、move カーソルを表示する。
            pointerEvents: "auto",
            cursor: "move",
            touchAction: "none",
            boxSizing: "border-box",
          }}
        />
      </div>
      <div className="canvas-bottom-controls__row">
        <div ref={zoomRef} className="canvas-bottom-controls__zoom">
          <ZoomButtons />
        </div>
        {/* Connect Feedback の FAB（document.body 直下に別途描画される）のための空き領域。
            FAB の表示・非表示にかかわらず確保し、全体マップの右端を FAB の右端へそろえる。 */}
        <div ref={fabSlotRef} className="canvas-bottom-controls__fab-slot" aria-hidden="true" />
      </div>
    </div>
  );
}

/**
 * クライアント座標を、全体マップの内部（content box）の左上を原点とする座標へ変換する。
 * 枠線を含む外枠の左上を原点にすると、枠線の太さのぶんだけ換算がずれる。
 */
function toMapLocal(
  map: HTMLElement,
  clientX: number,
  clientY: number
): { readonly x: number; readonly y: number } {
  const rect = map.getBoundingClientRect();
  return { x: clientX - rect.left - map.clientLeft, y: clientY - rect.top - map.clientTop };
}

/**
 * 要素の内部（content box）の寸法を実測し、大きさの変化に追従する。
 * 実測できるまでは fallback を返す。ResizeObserver が無い環境では、マウント後の実寸と
 * 画面サイズの変更時の実寸を使う。隠れている要素は 0 を返す（呼び出し側で 0 除算を避ける）。
 * layoutKey が変わったとき（表示・非表示の切り替えなど）も測り直す。
 */
function useContentBoxSize(
  ref: RefObject<HTMLElement | null>,
  fallback: ContentSize,
  layoutKey: unknown
): ContentSize {
  const [size, setSize] = useState<ContentSize>(fallback);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const apply = (width: number, height: number): void => {
      setSize((previous) =>
        previous.width === width && previous.height === height ? previous : { width, height }
      );
    };
    // 描画前に一度測っておく（全体マップは padding を持たないため、client 寸法が content box に
    // 等しい）。client 寸法は整数へ丸められるため、小数の実寸は ResizeObserver の通知で受け取る。
    const measure = (): void => apply(element.clientWidth, element.clientHeight);
    measure();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (!entry) return;
      apply(entry.contentRect.width, entry.contentRect.height);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, layoutKey]);

  return size;
}

/**
 * 下段が画面の左右の余白の内側へ収まらない狭い表示かどうかを実測して返す。
 * ズームパネル・FAB 用の空き領域の実幅（ルート文字サイズで変わる）と、根要素の上限幅
 * （画面幅で変わる）を比べる。判定結果で両者の実幅は変わらないため、切り替えで振動しない。
 */
function useCompactBottomControls(
  rootRef: RefObject<HTMLElement | null>,
  zoomRef: RefObject<HTMLElement | null>,
  fabSlotRef: RefObject<HTMLElement | null>
): boolean {
  const [compact, setCompact] = useState(false);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const zoom = zoomRef.current;
    const fabSlot = fabSlotRef.current;
    const row = fabSlot?.parentElement;
    if (!root || !zoom || !fabSlot || !row) return undefined;
    const evaluate = (): void => {
      setCompact(
        needsCompactBottomControls({
          zoomWidth: zoom.getBoundingClientRect().width,
          gap: Number.parseFloat(getComputedStyle(row).columnGap) || 0,
          fabSlotWidth: fabSlot.getBoundingClientRect().width,
          availableWidth: Number.parseFloat(getComputedStyle(root).maxWidth),
        })
      );
    };
    evaluate();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", evaluate);
      return () => window.removeEventListener("resize", evaluate);
    }
    // 根要素は上限幅に詰まったとき（画面幅の変化）、ズームパネルと空き領域はルート文字サイズの
    // 変化で大きさが変わるため、3 つとも監視する。
    const observer = new ResizeObserver(evaluate);
    observer.observe(root);
    observer.observe(zoom);
    observer.observe(fabSlot);
    return () => observer.disconnect();
  }, [rootRef, zoomRef, fabSlotRef]);

  return compact;
}
